// Unit + resident directory state for the onboarding wizard, with CSV I/O.
//
// Wired to the real backend (legalcode_search/app/community_directory.py,
// added 2026-09-21) whenever a `communityId` is available -- add/remove/
// import all become real requests against
// /admin/communities/{id}/units|residents[...], and CSV export downloads
// the server's own rendering (so exported files always match what's
// actually stored, not a client-side re-serialization of possibly-stale
// local state). Falls back to pure local state (no `communityId` yet, or a
// request fails) exactly like the rest of this admin UI's demo/offline
// posture -- see useAdmin.js's uploadDocument for the same pattern.

import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import type { Resident, Unit } from "./types";
import {
  communityFromCsv, communityToCsv, downloadCsv, readCsvFile,
  residentsFromCsv, residentsToCsv, unitsFromCsv, unitsToCsv,
} from "./csv";
import type { Community } from "./types";

export interface DirectoryApi {
  units: Unit[];
  residents: Resident[];
  addUnit: (u: Omit<Unit, "id">) => void;
  removeUnit: (id: string) => void;
  addResident: (r: Omit<Resident, "id">) => void;
  removeResident: (id: string) => void;
  residentsOf: (unitId: string) => Resident[];
  boardCount: number;
  exportUnits: () => void;
  exportResidents: () => void;
  exportCommunity: (c: Partial<Community>) => void;
  importUnits: (file: File) => Promise<number>;
  importResidents: (file: File) => Promise<number>;
  importCommunity: (file: File) => Promise<Partial<Community>>;
}

export function useDirectory(
  communityId: string | null,
  seedUnits: Unit[] = [],
  seedResidents: Resident[] = [],
  onLog?: (line: string, kind?: "req" | "ok" | "err") => void
): DirectoryApi {
  const [units, setUnits] = useState<Unit[]>(seedUnits);
  const [residents, setResidents] = useState<Resident[]>(seedResidents);
  const log = useCallback((l: string, k: "req" | "ok" | "err" = "ok") => onLog?.(l, k), [onLog]);

  // Load whatever's already on file for this community when it first
  // becomes available (e.g. re-opening the wizard, or landing on step 04
  // after step 00 created the record).
  useEffect(() => {
    if (!communityId) return;
    let alive = true;
    api.listUnits(communityId)
      .then((res) => { if (alive) setUnits(res?.units || []); })
      .catch(() => { /* stay with whatever local state already has */ });
    api.listResidents(communityId)
      .then((res) => { if (alive) setResidents(res?.residents || []); })
      .catch(() => { /* stay with whatever local state already has */ });
    return () => { alive = false; };
  }, [communityId]);

  const addUnit = useCallback((u: Omit<Unit, "id">) => {
    if (!communityId) {
      setUnits((prev) => [...prev, { ...u, id: "u" + Date.now() }]);
      log("POST /admin/communities/{id}/units · " + u.addr + " (local, no community yet)", "req");
      return;
    }
    log("POST /admin/communities/" + communityId + "/units · " + u.addr, "req");
    api.addUnit(communityId, u)
      .then((doc) => {
        setUnits((prev) => [...prev, doc]);
        log("201 created · " + doc.addr, "ok");
      })
      .catch((err) => {
        // Still add locally so the wizard doesn't lose what was typed --
        // it just won't be persisted until the next successful sync.
        setUnits((prev) => [...prev, { ...u, id: "u" + Date.now() }]);
        log((err.status || "ERR") + " " + err.message, "err");
      });
  }, [communityId, log]);

  const removeUnit = useCallback((id: string) => {
    setUnits((prev) => prev.filter((u) => u.id !== id));
    setResidents((prev) => prev.map((r) => ({ ...r, units: r.units.filter((x) => x !== id) })));
    if (!communityId) return;
    api.removeUnit(communityId, id).catch((err) => log((err.status || "ERR") + " " + err.message, "err"));
  }, [communityId, log]);

  const addResident = useCallback((r: Omit<Resident, "id">) => {
    if (!communityId) {
      setResidents((prev) => [...prev, { ...r, id: "p" + Date.now() }]);
      log("POST /admin/communities/{id}/residents · " + r.name + " (local, no community yet)", "req");
      return;
    }
    log("POST /admin/communities/" + communityId + "/residents · " + r.name + (r.board ? " · board" : ""), "req");
    api.addResident(communityId, r)
      .then((doc) => {
        setResidents((prev) => [...prev, doc]);
        log("201 created · " + doc.name, "ok");
      })
      .catch((err) => {
        setResidents((prev) => [...prev, { ...r, id: "p" + Date.now() }]);
        log((err.status || "ERR") + " " + err.message, "err");
      });
  }, [communityId, log]);

  const removeResident = useCallback((id: string) => {
    setResidents((prev) => prev.filter((r) => r.id !== id));
    if (!communityId) return;
    api.removeResident(communityId, id).catch((err) => log((err.status || "ERR") + " " + err.message, "err"));
  }, [communityId, log]);

  const residentsOf = useCallback(
    (unitId: string) => residents.filter((r) => r.units.includes(unitId)),
    [residents]
  );

  const importUnits = useCallback(async (file: File) => {
    if (!communityId) {
      // No community yet to import into server-side -- fall back to the
      // client-only parse so the wizard is still usable before step 00
      // has run (shouldn't normally happen, since Units is step 04).
      const rows = unitsFromCsv(await readCsvFile(file));
      setUnits(rows);
      setResidents([]);
      log("200 ok (local) · " + rows.length + " units parsed from " + file.name);
      return rows.length;
    }
    const res = await api.importUnits(communityId, file);
    const fresh = await api.listUnits(communityId);
    setUnits(fresh?.units || []);
    log(
      "200 ok · " + res.imported + " units imported" + (res.skipped ? ", " + res.skipped + " skipped" : "") +
      " from " + file.name,
      res.skipped ? "err" : "ok"
    );
    return res.imported;
  }, [communityId, log]);

  const importResidents = useCallback(async (file: File) => {
    if (!communityId) {
      const text = await readCsvFile(file);
      let count = 0;
      setResidents(() => {
        const rows = residentsFromCsv(text, units);
        count = rows.length;
        return rows;
      });
      log("200 ok (local) · residents parsed from " + file.name);
      return count;
    }
    const res = await api.importResidents(communityId, file);
    const fresh = await api.listResidents(communityId);
    setResidents(fresh?.residents || []);
    log(
      "200 ok · " + res.imported + " residents imported" + (res.skipped ? ", " + res.skipped + " skipped" : "") +
      " from " + file.name,
      res.skipped ? "err" : "ok"
    );
    return res.imported;
  }, [communityId, units, log]);

  const importCommunity = useCallback(async (file: File) => {
    const c = communityFromCsv(await readCsvFile(file));
    log("200 ok · community record read from " + file.name);
    return c;
  }, [log]);

  return useMemo(() => ({
    units, residents, addUnit, removeUnit, addResident, removeResident, residentsOf,
    boardCount: residents.filter((r) => r.board).length,
    exportUnits: () => {
      if (communityId) window.open(api.unitsCsvUrl(communityId), "_blank");
      else downloadCsv("units.csv", unitsToCsv(units));
    },
    exportResidents: () => {
      if (communityId) window.open(api.residentsCsvUrl(communityId), "_blank");
      else downloadCsv("residents.csv", residentsToCsv(residents, units));
    },
    exportCommunity: (c: Partial<Community>) => downloadCsv("community.csv", communityToCsv(c)),
    importUnits, importResidents, importCommunity,
  }), [communityId, units, residents, addUnit, removeUnit, addResident, removeResident, residentsOf, importUnits, importResidents, importCommunity]);
}
